<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div class="space-y-2">
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
          <BaseInput
            v-model="modifiedData.name"
            color="base"
            show-error-as-tooltip
            :error="getInputErrors('name')" />
        </div>
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
          <BaseInput
            v-model="modifiedData.code"
            color="base"
            show-error-as-tooltip
            :error="getInputErrors('code')" />
        </div>

        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="vatCategory" class="text-sm font-medium">{{ $t('vatCategory') }}</label>
          <BaseSelectDropdown
            name="vatCategory"
            :items="dropdownVatCategories"
            :selected-item="selectedVatCategory"
            show-error-as-tooltip
            clearable
            :error="getInputErrors('vatCategory')"
            @update:selected="handleVatCategorySelection" />
        </div>
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="vatLevel" class="text-sm font-medium">{{ $t('vatLevel') }}</label>
          <BaseSelectDropdown
            name="vatLevel"
            :items="dropdownVatLevels"
            :selected-item="selectedVatLevel"
            clearable
            show-error-as-tooltip
            :error="getInputErrors('vatLevel')"
            @update:selected="handleVatLevelSelection" />
        </div>

        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="vatLevel" class="text-sm font-medium">{{ $t('mw-params.value') }}</label>
          <BaseInput
            v-model="modifiedData.vatFactorValue"
            color="base"
            flat
            type="number"
            show-error-as-tooltip
            :error="getInputErrors('vatFactorValue')"
            :disabled="!modifiedData.isFromManager" />
        </div>

        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="boid" class="text-sm font-medium">{{ $t('baseObject.boid') }}</label>
          <BaseInput v-model="modifiedData.boid" color="base" />
        </div>

        <BaseToggle
          :label="$t('baseObject.isActive')"
          name="isActive"
          v-model="modifiedData.isActive"
          light
          :disable="!modifiedData.isFromManager" />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { addOrEditVatFactor } from '@/services/api/vat';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, minValue } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';

import type { VatFactor } from '@/types/Vat';
import { type SelectDropdownItem } from '@/types/Base';

import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    vatFactor: VatFactor;
  }>(),
  {}
);

const modifiedData = ref<VatFactor>(deepCopy<VatFactor>(props.vatFactor));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.vatLevel = newData.vatLevelId || '';
    validationModel.value.vatCategory = newData.vatCategoryId || '';
    validationModel.value.vatFactorValue = newData.vatFactorValue || 0;
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';

    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.vatFactor);
    isModified.value = valueChanged || $v.value.$invalid;
  },
  { deep: true }
);

const { dropdownVatLevels, dropdownVatCategories } = storeToRefs(useLookupsStore());
const { getVatLevelsLookup, getVatCategoriesLookup } = useLookupsStore();

onMounted(async () => {
  await getVatLevelsLookup();
  const vatLevelValue = dropdownVatLevels.value.find(
    (item) => item.value == modifiedData.value.vatLevelId
  );
  if (vatLevelValue) selectedVatLevel.value = vatLevelValue;

  await getVatCategoriesLookup();
  const vatCategoryValue = dropdownVatCategories.value.find(
    (item) => item.value == modifiedData.value.vatCategoryId
  );
  if (vatCategoryValue) selectedVatCategory.value = vatCategoryValue;
});

const selectedVatLevel = ref<SelectDropdownItem | null>(null);

function handleVatLevelSelection(item: SelectDropdownItem) {
  selectedVatLevel.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.vatLevelId = item ? item.value.toString() : '';
    modifiedData.value.vatLevel = item
      ? {
          id: item.value.toString(),
          name: item.name,
        }
      : undefined;
  }
}

const selectedVatCategory = ref<SelectDropdownItem | null>(null);

function handleVatCategorySelection(item: SelectDropdownItem) {
  selectedVatCategory.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.vatCategoryId = item ? item.value.toString() : '';
    modifiedData.value.vatCategory = item
      ? {
          id: item.value.toString(),
          name: item.name,
        }
      : undefined;
  }
}

function onDismissChanges() {
  modifiedData.value = deepCopy<VatFactor>(props.vatFactor);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditVatFactor(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
}

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
  vatLevel: modifiedData.value.vatLevelId || '',
  vatCategory: modifiedData.value.vatCategoryId || '',
  vatFactorValue: modifiedData.value.vatFactorValue || 0,
});

const rules = computed(() => ({
  vatLevel: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  vatCategory: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  vatFactorValue: {
    minValue: helpers.withMessage('Min Value is 0', minValue(0)),
  },
  name: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  code: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function getInputErrors(type: 'vatLevel' | 'vatCategory' | 'vatFactorValue' | 'name' | 'code') {
  if ($v.value[type].required && $v.value[type].required.$invalid)
    return $v.value[type].required.$message;
  else if ($v.value[type].minValue && $v.value[type].minValue.$invalid)
    return $v.value[type].minValue.$message;
  else return '';
}
</script>
